import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

import { Store } from '@ngrx/store';
import { BehaviorSubject, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';

import { AuthService } from '@auth/auth.service';
import { Login, setApiVersion } from '@auth/store/auth.actions';
import { DataService } from '@data/data.service';

import { ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';

import pkg from '@package_json';

import { credentials, env } from '@environment';

import { SigninData } from '../shared/signin-data.interface';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  providers: [ConfirmationService],
})
export class LoginComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<void>();

  @ViewChild('messageDialog') messageDialog: ConfirmDialog;

  @Input() submitted = false;
  @Input() loginForm: FormGroup;

  apiVersion = new BehaviorSubject<string>(null);
  apiVersion$ = this.apiVersion.asObservable();

  version = new BehaviorSubject<string>(null);
  version$ = this.version.asObservable();

  environment = new BehaviorSubject<string>(null);
  environment$ = this.environment.asObservable();

  constructor(
    private store: Store,
    private authService: AuthService,
    private dataService: DataService,
    private confirmationService: ConfirmationService
  ) {
    window.sessionStorage.clear();
    window.localStorage.clear();

    this.dataService
      .getApiInfo()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: any) => {
        this.store.dispatch(new setApiVersion(data.version));

        this.apiVersion.next(data.version);
        this.version.next(pkg.version);
        this.environment.next(env);
      });

    this.dataService
      .getImage('background')
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        if (response.status === 204 || response.status >= 400) {
          const layer1: HTMLElement = document.querySelector('.background-layer-1');
          layer1.style.opacity = '1.0';
          return;
        }

        const reader = new FileReader();
        reader.readAsDataURL(response.body);
        reader.onloadend = () => {
          const layer2: HTMLElement = document.querySelector('.background-layer-2');
          layer2.style.backgroundImage = 'url(' + reader.result.toString() + ')';
          layer2.style.opacity = '1.0';
        };
      });
  }

  ngOnInit() {
    this.loginForm = new FormGroup({
      username: new FormControl(),
      password: new FormControl(),
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  login() {
    if (!this.loginForm.get('username').value || !this.loginForm.get('password').value) {
      this.showMessage({ title: 'Error', message: 'Username and password cannot be blank' });
      return;
    }

    this.submitted = true;

    this.authService
      .login(this.loginForm.get('username').value.toUpperCase(), this.loginForm.get('password').value)
      .pipe(
        catchError((error: any) => {
          this.submitted = false;
          this.showMessage({ title: 'Error', message: `Login failed: ${error.error?.error?.message}` });
          return [];
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((data: SigninData) => {
        this.loginForm.get('username').setValue('');
        this.loginForm.get('password').setValue('');

        this.store.dispatch(new Login(data));
      });
  }

  automaticLogin = () => {
    this.loginForm.get('username').setValue(credentials.username);
    this.loginForm.get('password').setValue(credentials.password);
  };

  showMessage(options: any) {
    this.confirmationService.confirm({
      key: 'auth-message',
      header: options.title,
      message: options.message,
      accept: () => {},
    });
  }
}
